import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Pagination,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CustomCard from "../../../../components/app/settings/user-settings/CustomCard";
import { ReactNode, useState } from "react";
import useDisclosure from "../../../../hooks/use-disclosure";
import Flex from "../../../../components/Flex";
import { ScrollArea } from "../../../../components/ScrollBar";
import useCustomPagination from "../../../../hooks/use-custom-pagination";
import CustomSelect from "../../../../components/CustomSelect";
import CustomTitle from "../../../../components/app/CustomTitle";
import StyledButtonV2 from "../../../../components/app/StyledButtonV2";
import SearchIcon from "@mui/icons-material/Search";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import { CurView } from "./tenant-providers";
import AdminUserTable from "./AdminUserTable";
import UserProfileDialog from "../../../../components/app/settings/user-settings/UserProfileDrawer";
import { useUsersList } from "../../../../queries/use-get-users";
import { useDebounce } from "../../../../hooks/use-debounce";
import UpsertStaffUserDrawer from "../../../../components/app/admin/provider-groups/UpsertStaffUserDrawer";
import {
  DEFAULT_VALUE_LABEL_LIST,
  getUnNecessaryTransformData,
  transformMultiSelectFrontEndVal,
} from "../../../../utils/utils";
import { useUpdateUser } from "../../../../queries/use-update-user";
import { STATUS } from "../../../../constants/AutocompleteOptions";
import { useAddStaffUser } from "../../../../queries/use-add-staff-user";
import { STAFF_USER_FORM_INITIAL_VALUES } from "../../../../constants/staff-user-schema";
import { UserCardData } from "../../../../types/user-types";

const AdminUser = () => {
  const USERS_PER_PAGE = 8;
  const [statusFilter, setStatusFilter] = useState<boolean | string>("all");

  const [searchKey, setSearchKey, instantSearchVal] = useDebounce<string>("");

  const [selectedUserData, setSelectedUserData] = useState<UserCardData | null>(
    null
  );
  const [modalTitle, setModalTitle] = useState<"Add New User" | "Edit User">(
    "Add New User"
  );

  const [userStatus, setUserStatus] = useState<boolean>(false);

  const [pagination, setPagination] = useCustomPagination({
    currentPage: 0,
    totalRecords: 0,
    recordsPerPage: USERS_PER_PAGE,
  });
  const { data: UsersList } = useUsersList({
    pagination: {
      page: pagination.currentPage,
      pageSize: pagination.recordsPerPage,
    },
    searchKey,
    statusKey:
      statusFilter === "all"
        ? undefined
        : typeof statusFilter === "boolean"
          ? statusFilter
          : undefined,
  });
  const {
    opened: openedStaffUserModal,
    open: openStaffUserModal,
    close: closeStaffUserModal,
  } = useDisclosure(false);

  function handleChange(_: React.ChangeEvent<unknown>, pageNumber: number) {
    setPagination((prevPage) => ({
      ...prevPage,
      currentPage: pageNumber - 1,
    }));
  }

  function handleOpenEditModal(userData: UserCardData) {
    setSelectedUserData(userData as any);
    setModalTitle("Edit User");
    openStaffUserModal();
  }

  function handleStatusChange(userId: string, status: boolean) {
    const user = UsersList?.data?.content?.find((user) => user.id === userId);
    if (user) {
      user.status = status;
      setUserStatus(!userStatus);
    }
    return new Promise(() => {});
  }

  const { mutateAsync: updateStaffUser } = useUpdateUser();
  const { mutateAsync: addStaffUser } = useAddStaffUser();

  const editStaffUser = (values: any) => {
    const transFormData: any = getUnNecessaryTransformData(
      ["workLocations"],
      values
    );
    if (modalTitle === "Add New User")
      return addStaffUser({ user: transFormData }).then((res) => {
        return res;
      });
    return updateStaffUser({
      user: transFormData,
    }).then((res) => {
      // refetch();
      return res;
    });
  };
  const userProfileModalDisclosure = useDisclosure(false);
  const [curView, setCurView] = useState<CurView>("card_view");

  const ACTIVE_VIEW_MAPPER: Record<CurView, ReactNode> = {
    card_view: (
      <Flex
        flexWrap={"wrap"}
        width={"100%"}
        alignItems={"center"}
        justifyContent={
          UsersList?.data?.content?.length &&
          UsersList?.data?.content?.length < 4
            ? "left"
            : "center"
        }
        gap={1}
      >
        {UsersList?.data?.content?.map((user) => {
          return (
            <Grid item key={user.value}>
              <CustomCard
                userData={user}
                handleOpenEditModal={() => handleOpenEditModal(user)}
                handleOpenProfileModal={() => {
                  setSelectedUserData(user);
                  userProfileModalDisclosure.open();
                }}
                onStatusChange={() => handleStatusChange(user.id, user.status)}
              />
            </Grid>
          );
        })}
      </Flex>
    ),
    table_view: (
      <AdminUserTable searchKey={searchKey} statusFilter={statusFilter} />
    ),
  };

  return (
    <Box>
      <Flex justifyContent={"space-between"} alignItems={"center"}>
        <CustomTitle>Admin Users</CustomTitle>
        <Flex gap={2} alignItems={"center"}>
          <TextField
            placeholder="Search"
            variant="outlined"
            value={instantSearchVal}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              setSearchKey(e.currentTarget.value);
            }}
          />
          <Box width={"35%"}>
            <CustomSelect
              value={statusFilter as any}
              options={STATUS as any}
              placeholder="Status"
              onChange={(v) => setStatusFilter(v as any)}
            />
          </Box>

          {/* TODO need to build custom component for multiple user and reduce snippets */}
          <ToggleButtonGroup
            value={curView}
            exclusive
            onChange={(_, v) => v && setCurView(v)}
            sx={{
              height: "38px",
              "& .css-ueukts-MuiButtonBase-root-MuiToggleButton-root.Mui-selected":
                {
                  backgroundColor: "#004186",
                  color: "#fff",
                },
              "&  .css-ueukts-MuiButtonBase-root-MuiToggleButton-root.Mui-selected:hover":
                {
                  backgroundColor: "#004186",
                },
            }}
          >
            <ToggleButton value="table_view">
              <ViewListOutlinedIcon />
            </ToggleButton>
            <ToggleButton value="card_view">
              <GridViewOutlinedIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          <StyledButtonV2
            label="Add Admin user"
            sx={{ width: "16rem" }}
            onClick={() => {
              setModalTitle("Add New User");
              openStaffUserModal();
            }}
            shouldAddIcon
          />
        </Flex>
      </Flex>

      <ScrollArea marginTop={"10px"} height="calc(100vh - 235px)">
        {ACTIVE_VIEW_MAPPER[curView]}
      </ScrollArea>

      {curView === "card_view" && (
        <Flex
          justifyContent={{
            sm: "center",
            md: "flex-end",
          }}
          position={"fixed"}
          bottom={"30px"}
          right={"18px"}
          width={"96%"}
          bgcolor={"white"}
          zIndex={"1000"}
          padding={"0px 5px 5px 0px"}
          alignItems={"baseline"}
        >
          <Pagination
            count={UsersList?.data?.page?.totalPages}
            page={pagination.currentPage + 1}
            onChange={handleChange}
            color="primary"
            shape="rounded"
          />
        </Flex>
      )}

      <UserProfileDialog
        disClosureHost={userProfileModalDisclosure}
        userId={(selectedUserData as any)?.uuid}
        dialogTitle="User Profile"
      />

      <UpsertStaffUserDrawer
        open={openedStaffUserModal}
        modalTitle={modalTitle}
        onClose={closeStaffUserModal}
        initialValues={
          modalTitle === "Edit User"
            ? {
                ...(selectedUserData as any),
                workLocations: (selectedUserData as any)?.workLocations
                  ? transformMultiSelectFrontEndVal(
                      DEFAULT_VALUE_LABEL_LIST,
                      (selectedUserData as any)?.workLocations
                    )
                  : undefined,
              }
            : {
                ...STAFF_USER_FORM_INITIAL_VALUES,
              }
        }
        onSubmit={editStaffUser}
      />
    </Box>
  );
};

export default AdminUser;
